import * as RSelect from "@radix-ui/react-select";
import type { ReactNode } from "react";

export interface SelectOption<V extends string> {
  value: V;
  label: ReactNode;
}

export interface SelectProps<V extends string> {
  label: string;
  value: V | undefined;
  options: ReadonlyArray<SelectOption<V>>;
  onValueChange: (value: V | undefined) => void;
  placeholder?: string;
  triggerClassName?: string;
  contentClassName?: string;
  itemClassName?: string;
  /**
   * If `clearable`, an "All / none" option (value `""`) is rendered first.
   * The wrapper translates the empty string back to `undefined` for the
   * caller, matching the existing chip-filter pattern.
   */
  clearable?: boolean;
  disabled?: boolean;
}

const SENTINEL_CLEAR = "__clear__" as const;

export function Select<V extends string>({
  label,
  value,
  options,
  onValueChange,
  placeholder,
  triggerClassName,
  contentClassName,
  itemClassName,
  clearable = true,
  disabled = false,
}: SelectProps<V>): JSX.Element {
  return (
    <RSelect.Root
      value={value ?? SENTINEL_CLEAR}
      onValueChange={(v) =>
        onValueChange(v === SENTINEL_CLEAR ? undefined : (v as V))
      }
      disabled={disabled}
    >
      <RSelect.Trigger
        className={triggerClassName ?? "ds-select__trigger"}
        aria-label={label}
      >
        <RSelect.Value placeholder={placeholder ?? `+ ${label}`} />
        <RSelect.Icon className="ds-select__icon" aria-hidden="true">
          ⌄
        </RSelect.Icon>
      </RSelect.Trigger>
      <RSelect.Portal>
        <RSelect.Content
          className={contentClassName ?? "ds-select__content"}
          position="popper"
          sideOffset={4}
        >
          <RSelect.Viewport>
            {clearable && (
              <RSelect.Item
                value={SENTINEL_CLEAR}
                className={itemClassName ?? "ds-select__item"}
              >
                <RSelect.ItemText>{placeholder ?? `+ ${label}`}</RSelect.ItemText>
              </RSelect.Item>
            )}
            {options.map((o) => (
              <RSelect.Item
                key={o.value}
                value={o.value}
                className={itemClassName ?? "ds-select__item"}
              >
                <RSelect.ItemText>{o.label}</RSelect.ItemText>
              </RSelect.Item>
            ))}
          </RSelect.Viewport>
        </RSelect.Content>
      </RSelect.Portal>
    </RSelect.Root>
  );
}
